import React, { FC, useState } from "react";
import { Tabs, TabsProps, Card, Button, Space, Input, Select } from "antd";
import { UserOutlined, ClockCircleOutlined, CloseCircleOutlined, PlusOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { useGetUsers } from "../utils/hooks";
import { UserProps } from "../utils/types";
import { getUsers } from "../utils/functions";
import AdminApprovalPanel from "../components/AdminApprovalPanel";
import {UserManagement} from "../components/UserManagement";
import DeniedUsersPanel from "../components/DeniedUsersPanel";
import { useNavigate } from 'react-router-dom';
import { fullname, role, email } from "../utils/data";
import skyBg from '../assets/images/background/SkyWithClouds.png';
import skyBgMobile from '../assets/images/background/SkyWithCloudsMobile.png';
import Header from "../components/Header";
import ArrowRight from "../components/svg/ArrowRight";
import ArrowRightWhite from "../components/svg/ArrowRightWhite";

const { Option } = Select;

interface UserPageProps {
  initialTab?: 'approved' | 'pending' | 'denied';
}

const User: FC<UserPageProps> = ({ initialTab }) => {
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [users, setUsers] = useState<UserProps[] | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  
  const currentUser = {
    role: localStorage.getItem(role) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(email) || ''
  };

  // Get initial tab from localStorage if available, otherwise use prop or default
  const getInitialTab = () => {
    const storedTab = localStorage.getItem('userManagementTab') as 'approved' | 'pending' | null;
    if (storedTab) {
      localStorage.removeItem('userManagementTab'); // Clear after reading
      return storedTab;
    }
    return initialTab || "approved";
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());

  useGetUsers(setUsers, setFetching);

  const refreshUsers = () => {
    getUsers(setUsers, setFetching);
  };

  // Separate users by approval status
  const pendingUsers = users?.filter(user => !user.approved && !user.denial_reason) || [];
  const approvedUsers = users?.filter(user => user.approved) || [];
  const deniedUsers = users?.filter(user => !user.approved && user.denial_reason) || [];

  const tabItems: TabsProps['items'] = [
    {
      key: 'approved',
      label: (
        <span className="flex items-center gap-2 font-['Lexend',sans-serif] font-medium">
          <UserOutlined />
          User Management ({approvedUsers.length})
        </span>
      ),
      children: (
        <UserManagement
          approvedUsers={approvedUsers}
          onRefreshUsers={refreshUsers}
          onEditUser={() => {}} 
          loading={fetching}
          searchTerm={searchTerm}
          selectedRole={selectedRole}
        />
      )
    },
    {
      key: 'pending',
      label: (
        <span className="flex items-center gap-2 font-['Lexend',sans-serif] font-medium">
          <ClockCircleOutlined />
          Pending Approvals ({pendingUsers.length})
        </span>
      ),
      children: (
        <AdminApprovalPanel
          pendingUsers={pendingUsers}
          onRefreshUsers={refreshUsers}
          loading={fetching}
          searchTerm={searchTerm}
          selectedRole={selectedRole}
        />
      )
    },
    {
      key: 'denied',
      label: (
        <span className="flex items-center gap-2 font-['Lexend',sans-serif] font-medium">
          <CloseCircleOutlined />
          Denied Requests ({deniedUsers.length})
        </span>
      ),
      children: (
        <DeniedUsersPanel
          deniedUsers={deniedUsers}
          onRefreshUsers={refreshUsers}
          loading={fetching}
          searchTerm={searchTerm}
          selectedRole={selectedRole}
        />
      )
    }
  ];

  return (
    <div className="relative min-h-screen w-full overflow-y-auto overflow-x-hidden flex flex-col bg-gradient-to-b from-[#a4c8e1] via-[#dceaf3] to-white font-['DM_Sans',sans-serif]">
      {/* Background */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover md:min-h-[220px] hidden md:block" />
        <img src={skyBgMobile} alt="" className="w-full h-auto object-cover md:hidden" />
      </div>

      {/* Header */}
      <Header currentUser={currentUser} showAdmin={false} />


      {/* Main Content */}
     <main className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12">
        <div>
          {/* Page Title & Stats */}
          {/* Combined Header and Search Card */}
          <div className="premium-card white w-full px-[18px] pt-[22px] pb-[31px] md:px-[24px] md:py-[28px] mb-[29px] md:mb-[34px]">
            <div className="flex flex-col gap-8">
              {/* Header Row */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-[#4682B4]/10 p-2 rounded-xl">
                      <UserOutlined className="text-[24px] text-[#4682B4]" />
                    </div>
                    <h1 className="font-['Lexend',sans-serif] text-[14px] md:text-[20px] font-bold text-[#1F2A37] m-0">User Management</h1>
                  </div>
                  <p className="text-[#64748b] text-[12px] md:text-[14px] font-medium m-0 max-w-[600px]">
                    Manage user accounts, roles, and permissions for the employee management system
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <button 
                    className="bg-[#4682B4] text-white px-6 py-3 rounded-full font-['Lexend',sans-serif] font-semibold text-[14px] shadow-[0px_8px_20px_rgba(70,130,180,0.2)] hover:bg-[#3a6d96] hover:-translate-y-0.5 transition-all active:scale-95 border-none cursor-pointer flex items-center gap-2"
                  >
                    <PlusOutlined />
                    Add New User
                  </button>
                </div>
              </div>


              {/* Search & Filter Row */}
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-[20px] md:gap-[24px] items-end">
                <div className="md:col-span-2 flex flex-col">
                  <label className="text-[12px] md:text-[14px] font-medium text-[#1F2A37] mb-[11px] md:mb-[16px] font-['Lexend']">Search Users</label>
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-[45px] rounded-[8px] bg-[#F3F4F6] border-none"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[12px] md:text-[14px] font-medium text-[#1F2A37] mb-[11px] md:mb-[16px] font-['Lexend']">Filter by Role</label>
                  <style dangerouslySetInnerHTML={{__html: `
                    .custom-select .ant-select-selector {
                      height: 45px !important;
                      background-color: #F3F4F6 !important;
                      border: none !important;
                      border-radius: 8px !important;
                      display: flex !important;
                      align-items: center !important;
                      padding: 0 16px !important;
                    }
                    .custom-select .ant-select-selection-item {
                      line-height: 45px !important;
                    }
                  `}} />
                  <Select
                    value={selectedRole}
                    onChange={setSelectedRole}
                    className="w-full h-[45px] custom-select"
                    placeholder="All Roles"
                  >
                    <Option value="all">All Roles</Option>
                    <Option value="Admin">Admin</Option>
                    <Option value="User">User</Option>
                  </Select>
                </div>
                <div className="flex justify-between md:justify-start gap-2  md:gap-[24px] xl:gap-[10px] 2xl:gap-[24px] mt-[29px] md:mt-0">
                  <Button 
                    className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-[#4682B4] border border-[#4682B4] hover:bg-[#3b6d96] text-white font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] border-none cursor-pointer transition-colors h-[32px] md:h-[47px]"
                    onClick={() => {}}
                  >
                     <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] rounded-full bg-white flex items-center justify-center text-[#4682B4]">
                      <div className="scale-[0.6] md:scale-100 flex items-center justify-center"><ArrowRight /></div>
                      </div>
                    Search
                  </Button>
                  <Button 
                    className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-white border border-[#4682B4] text-[#1F2A37] font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] cursor-pointer transition-colors hover:bg-[#f8FAFC] h-[32px] md:h-[47px]"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedRole('all');
                    }}
                  >
                    <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] flex items-center justify-center">
                      <div className="scale-[0.73] md:scale-100 flex items-center justify-center origin-center"><ArrowRightWhite /></div>
                    </div>
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex flex-col md:flex-row gap-5 mb-8">
            <div className="premium-card blue-tint w-full p-[24px]">
                <div className="text-[12px] font-semibold text-[#4682B4] uppercase tracking-wider mb-1.5 text-center">Total Users</div>
                <div className="text-[24px] font-semibold text-[#4682B4] leading-none text-center">{(users?.length || 0)}</div>
              </div>
              <div className="premium-card blue-tint w-full p-[24px]">
                <div className="text-[12px] font-semibold text-[#10b981] uppercase tracking-wider mb-1.5 text-center">Approved</div>
                <div className="text-[24px] font-semibold text-[#10b981] leading-none text-center">{approvedUsers.length}</div>
              </div>
              <div className="premium-card blue-tint w-full p-[24px]">
                <div className="text-[12px] font-semibold text-[#f59e0b] uppercase tracking-wider mb-1.5 text-center">Pending</div>
                <div className="text-[24px] font-semibold text-[#f59e0b] leading-none text-center">{pendingUsers.length}</div>
              </div>
          </div>

          {/* Tabs Content */}
          <div className="premium-card blue-tint p-4 overflow-hidden mb-8">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as 'approved' | 'pending' | 'denied')}
              items={tabItems}
              size="large"
              className="user-management-tabs [&_.ant-tabs-nav]:px-8 [&_.ant-tabs-nav]:pt-4 [&_.ant-tabs-ink-bar]:bg-[#4682B4] [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#4682B4] [&_.ant-tabs-tab]:!font-['Lexend',sans-serif] [&_.ant-tabs-tab]:!font-semibold"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default User;